import React, { useEffect, useState } from "react";
import { IFine, CreateFineRequest } from "../../types/fine.type";
import { api } from "../../api/api";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Typography,
  message,
  DatePicker,
  Popconfirm,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Currency } from "../../types/currency.type";
import { ISalary } from "../../types/salary.type";
import useGetRequest from "../../hooks/useGetRequest";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

const { Title } = Typography;
const { TextArea } = Input;

export default function Fines() {
  const { itemId, name } = useParams<{ itemId: string; name: string }>();
  const [fines, setFines] = useState<IFine[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState<ISalary | null>(null);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [form] = Form.useForm();

  const { data: salaryData, loading: salaryLoading } = useGetRequest(`/salary/worker/${itemId}`);

  useEffect(() => {
    if (salaryData?.salary) {
      setSelectedWorker(salaryData.salary);
    }
  }, [salaryData]);

  const fetchFines = async () => {
    if (!itemId) {
      setFines([]);
      setTotal(0);
      return;
    }

    setLoading(true);
    try {
      const params: Record<string, any> = {
        page,
        limit: pageSize,
        workerId: itemId,
      };

      if (dateRange && dateRange[0] && dateRange[1]) {
        params.startDate = dateRange[0].format("DD.MM.YY");
        params.endDate = dateRange[1].format("DD.MM.YY");
      }

      const response = await api.get("/fine", { params });

      setFines(response.data.fines || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching fines", error);
      message.error("Ошибка при получении штрафов");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFines();
  }, [page, pageSize, itemId, dateRange]);

  const handleCreateFine = async (values: CreateFineRequest) => {
    if (!itemId) {
      message.error("Сотрудник не выбран");
      return;
    }

    setConfirmLoading(true);
    try {
      const data = {
        ...values,
        workerId: itemId,
        date: values.date || dayjs().format("DD.MM.YYYY"),
      };

      await api.post("/fine", data);

      message.success("Штраф успешно создан.");
      setIsModalOpen(false);
      form.resetFields();
      fetchFines();
    } catch (error: any) {
      console.error("Error creating fine:", error);
      message.error(error.response?.data?.error || "Ошибка при создании штрафа");
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleDeleteFine = async (fineId: string) => {
    try {
      await api.delete(`/fine/${fineId}`);
      message.success("Штраф успешно удален.");
      fetchFines();
    } catch (error: any) {
      console.error("Error deleting fine:", error);
      message.error(error.response?.data?.error || "Ошибка при удалении штрафа");
    }
  };

  const handleOpenModal = () => {
    if (!itemId || !selectedWorker) {
      message.error("Сотрудник не найден");
      return;
    }
    setIsModalOpen(true);
    form.resetFields();
    form.setFieldsValue({
      date: dayjs(),
      currency: (selectedWorker as any)?.salaryCurrency || Currency.US_DOLLAR,
    });
  };

  const columns = [
    {
      title: "№",
      key: "index",
      width: 60,
      render: (_: any, __: any, index: number) => (page - 1) * pageSize + index + 1,
    },
    {
      title: "Сотрудник",
      key: "worker",
      render: (_: any, record: IFine) => (
        <div>
          <div>{record.workerName}</div>
          {record.workerSurname && (
            <div style={{ fontSize: "12px", color: "#888" }}>{record.workerSurname}</div>
          )}
        </div>
      ),
    },
    {
      title: "Сумма",
      key: "amount",
      render: (_: any, record: IFine) => (
        <div>
          {record.amount.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}{" "}
          {record.currency}
        </div>
      ),
    },
    {
      title: "Описание",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "Дата",
      dataIndex: "date",
      key: "date",
      width: 120,
    },
    {
      title: "Создал",
      dataIndex: "createdByName",
      key: "createdByName",
    },
    {
      title: "Дата создания",
      key: "createdAt",
      width: 180,
      render: (_: any, record: IFine) =>
        record.createdAt
          ? dayjs(record.createdAt).format("DD.MM.YYYY HH:mm")
          : "-",
    },
    {
      title: "Действия",
      key: "actions",
      width: 100,
      fixed: "right" as "right",
      render: (_: any, record: IFine) => (
        <Popconfirm
          title="Вы уверены, что хотите удалить этот штраф?"
          description="Баланс сотрудника будет восстановлен."
          onConfirm={() => handleDeleteFine(record._id)}
          okText="Да"
          cancelText="Нет"
        >
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            size="small"
          >
            Удалить
          </Button>
        </Popconfirm>
      ),
    },
  ];

  if (!itemId) {
    return (
      <div style={{ padding: 24 }}>
        <Title level={2}>Штрафы</Title>
        <div>Сотрудник не найден</div>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <Title level={2}>
          Штрафы: {selectedWorker ? `${selectedWorker.workerName} ${selectedWorker.workerSurname || ""}` : name || "Загрузка..."}
        </Title>
        {(selectedWorker || !salaryLoading) && (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenModal}>
            Добавить штраф
          </Button>
        )}
      </div>

      <Space style={{ marginBottom: 16 }} wrap>
        <DatePicker.RangePicker
          format="DD.MM.YY"
          value={dateRange}
          onChange={(dates) => {
            setDateRange(dates);
            setPage(1);
          }}
          allowClear
        />
      </Space>

      <Table
        loading={loading}
        columns={columns}
        dataSource={fines}
        rowKey={(record) => record._id}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
        }}
        scroll={{ x: "max-content" }}
      />

      <Modal
        title="Добавить штраф"
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
        width={600}
      >
        <Form
          form={form}
          onFinish={handleCreateFine}
          layout="vertical"
          initialValues={{
            date: dayjs(),
            currency: (selectedWorker as any)?.salaryCurrency || Currency.US_DOLLAR,
          }}
        >
          <Form.Item
            name="amount"
            label="Сумма штрафа"
            rules={[
              { required: true, message: "Введите сумму штрафа" },
              { type: "number", min: 0.01, message: "Сумма должна быть больше 0" },
            ]}
          >
            <InputNumber
              min={0}
              step={0.01}
              style={{ width: "100%" }}
              placeholder="Введите сумму"
            />
          </Form.Item>

          <Form.Item
            name="currency"
            label="Валюта"
            rules={[{ required: true, message: "Выберите валюту" }]}
          >
            <Select placeholder="Выберите валюту" disabled>
              {Object.values(Currency).map((currency) => (
                <Select.Option key={currency} value={currency}>
                  {currency}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="Описание"
            rules={[{ required: true, message: "Введите описание" }]}
          >
            <TextArea
              rows={4}
              placeholder="Введите описание штрафа"
            />
          </Form.Item>

          <Form.Item
            name="date"
            label="Дата"
            rules={[{ required: true, message: "Выберите дату" }]}
            getValueFromEvent={(date) => {
              if (!date) return undefined;
              return date.format("DD.MM.YYYY");
            }}
            getValueProps={(value) => {
              if (!value) return { value: undefined };
              return { value: dayjs(value, "DD.MM.YYYY") };
            }}
          >
            <DatePicker
              format="DD.MM.YYYY"
              style={{ width: "100%" }}
              placeholder="Выберите дату"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
